import type { Root, Element, Parent } from "hast";
import { isObject } from "lodash-es";
import { visitParents } from "unist-util-visit-parents";
import type { VFile } from "vfile";

/**
 * Matched against the raw markdown, before the tree rewrites `point_box` and
 * `track`, so the underscore spellings are the ones to use. Longest first: the
 * alternation is leftmost-first, so `point` would otherwise swallow `point_box`.
 */
const CUSTOM_TAGS = ["point_box", "collection", "polygon", "track", "point", "clip"];

interface StreamingMarkerOptions {
  hasFinished?: boolean;
}

/**
 * Marks the unclosed tag and its open ancestors with `data-streaming="true"`.
 *
 * Whether a tag is closed only means something while the response is arriving.
 * Once the run is over, whatever is open is all there will ever be, and marking
 * it leaves the shimmer running over output that is not coming.
 */
export const rehypeStreamingMarker = ({ hasFinished = false }: StreamingMarkerOptions = {}) => {
  return (tree: Root, file: VFile) => {
    if (hasFinished) return;

    const sourceText = String(file);
    const tagStack: string[] = [];

    // 1. Use the stack-based parser to determine IF the stream is incomplete.
    // This is the most reliable source of truth.
    // The boundary matters: without it `<points>` and `<clipPath>` count as
    // open custom tags and the response never balances.
    const tagRegex = new RegExp(`<(\\/)?(${CUSTOM_TAGS.join("|")})(?=[\\s/>])([^>]*)>`, "g");
    let match;
    while ((match = tagRegex.exec(sourceText)) !== null) {
      const isClosing = match[1] === "/";
      const tagName = match[2];
      // Opens and closes in one go; pushing it leaves nothing to pop it. No
      // input was found where this alone changed what is on screen.
      const isSelfClosing = (match[3] ?? "").trimEnd().endsWith("/");

      if (isClosing) {
        if (tagStack.length > 0 && tagStack[tagStack.length - 1] === tagName) {
          tagStack.pop();
        }
      } else if (!isSelfClosing) {
        tagStack.push(tagName ?? "");
      }
    }

    // If the stack is empty, every tag is closed. Do nothing.
    if (tagStack.length === 0) {
      return;
    }

    // 2. If we are streaming, find the path to the last parsed element.
    // This path represents the entire currently-open branch of the tree.
    let lastElement: Element | null = null;
    let lastElementAncestors: Parent[] = [];

    visitParents(tree, "element", (node, ancestors) => {
      // This visitor will be called for each element. The last one it's
      // called for is the last element in the depth-first traversal.
      lastElement = node as Element;
      lastElementAncestors = ancestors;
    });

    if (lastElement) {
      // 3. Mark every element on that path as streaming.
      const streamingPath = [...lastElementAncestors, lastElement];
      streamingPath.forEach((node) => {
        // Ensure we're only adding properties to elements
        if (node.type === "element" && "properties" in node && isObject(node.properties)) {
          node.properties = {
            ...node.properties,
            "data-streaming": "true",
          };
        }
      });
    }
  };
};
